// Progression compatibility validation
// Validates whether a chord progression is compatible with a scale/mode
// based on the required chord qualities at each scale degree
//
// DEAD CODE, exported but imported nowhere. It reads mode.chordQualities,
// which is wrong for the 10 pentatonic modes (the live pads go through
// getPentatonicChordTypes instead). Fix chord_qualities in the database
// before reviving any of these paths. See docs/plans/pentatonic-chord-naming.md.

import { MODES } from './musicTheory';
import { getProgressionByName } from './chordProgressions';

/**
 * Validates if a progression is compatible with a scale/mode
 * @param {string} progressionName - Name of the progression
 * @param {string} modeName - Name of the mode/scale
 * @returns {object} - { compatible: boolean, reasons: string[] }
 */
export const isProgressionCompatibleWithMode = (progressionName, modeName) => {
  const progression = getProgressionByName(progressionName);
  const mode = MODES[modeName];

  if (!progression) {
    return {
      compatible: false,
      reasons: [`Progression "${progressionName}" not found`]
    };
  }

  if (!mode) {
    return {
      compatible: false,
      reasons: [`Mode "${modeName}" not found`]
    };
  }

  // Check if mode explicitly lists this progression
  if (mode.progressions && mode.progressions.includes(progressionName)) {
    return {
      compatible: true,
      reasons: ['Progression is explicitly listed for this mode']
    };
  }

  // If not explicitly listed, validate chord qualities
  const reasons = [];
  let compatible = true;

  const chordQualities = mode.chordQualities;
  const degrees = progression.degrees;

  if (!chordQualities || chordQualities.length === 0) {
    return {
      compatible: false,
      reasons: ['Mode has no defined chord qualities']
    };
  }

  // Check each degree in the progression
  for (let i = 0; i < degrees.length; i++) {
    const degree = degrees[i];
    const degreeIndex = degree - 1; // Convert to 0-based index

    // Handle out-of-range degrees (for scales with fewer than 7 notes)
    if (degreeIndex >= chordQualities.length) {
      compatible = false;
      reasons.push(`Degree ${degree} (Roman numeral ${getRomanNumeral(degree)}) is out of range for this ${chordQualities.length}-note scale`);
      continue;
    }

    const expectedQuality = chordQualities[degreeIndex];

    // Check if the quality matches what the progression needs
    // This is a simplified check - in reality, progressions might work with
    // multiple quality types (e.g., a "ii-V-I" could work with min or dim on ii)
    if (!expectedQuality) {
      compatible = false;
      reasons.push(`No chord quality defined for degree ${degree} in this mode`);
    }
  }

  // If we passed all checks and no explicit listing, it's potentially compatible
  if (compatible && reasons.length === 0) {
    return {
      compatible: true,
      reasons: ['Chord qualities at required degrees are available']
    };
  }

  return {
    compatible,
    reasons
  };
};

/**
 * Get all compatible progressions for a given mode
 * @param {string} modeName - Name of the mode/scale
 * @returns {string[]} - Array of compatible progression names
 */
export const getCompatibleProgressions = (modeName) => {
  const mode = MODES[modeName];

  if (!mode) {
    console.warn(`Mode "${modeName}" not found`);
    return [];
  }

  // Return the explicitly defined progressions for this mode
  return mode.progressions || [];
};

/**
 * Helper function to get Roman numeral representation
 * @param {number} degree - Scale degree (1-based)
 * @returns {string} - Roman numeral
 */
const getRomanNumeral = (degree) => {
  const romanNumerals = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];
  return romanNumerals[(degree - 1) % romanNumerals.length] || degree.toString();
};

/**
 * Get detailed compatibility information for all progressions with a mode
 * @param {string} modeName - Name of the mode/scale
 * @returns {object[]} - Array of { progressionName, compatible, reasons }
 */
export const getAllProgressionCompatibility = (modeName) => {
  const mode = MODES[modeName];

  if (!mode) {
    return [];
  }

  const compatibleProgressions = getCompatibleProgressions(modeName);

  return compatibleProgressions.map(progressionName => {
    const validation = isProgressionCompatibleWithMode(progressionName, modeName);
    return {
      progressionName,
      ...validation
    };
  });
};

/**
 * Validate specific chord quality requirements for a progression
 * Useful for understanding WHY a progression works or doesn't work
 * @param {string} progressionName - Name of the progression
 * @param {string} modeName - Name of the mode/scale
 * @returns {object[]} - Array of { degree, requiredQuality, actualQuality, compatible }
 */
export const getProgressionChordQualities = (progressionName, modeName) => {
  const progression = getProgressionByName(progressionName);
  const mode = MODES[modeName];

  if (!progression || !mode) {
    return [];
  }

  const chordQualities = mode.chordQualities;
  const degrees = progression.degrees;

  return degrees.map(degree => {
    const degreeIndex = degree - 1;
    const actualQuality = chordQualities[degreeIndex];

    return {
      degree,
      romanNumeral: getRomanNumeral(degree),
      actualQuality: actualQuality || 'undefined',
      available: !!actualQuality,
      degreeIndex
    };
  });
};
